//@flow
import * as React from 'react';
import ErrorReporter from 'common/utils/ErrorReporter';
import { withRouter } from 'common/components/core/router';
import { translate } from 'react-i18next';
import type { TFunction } from 'react-i18next';

type Props = {
    errorBoundaryTitle?: string,
    errorBoundarySubtitle?: string,
    errorBoundaryActionLabel?: string,
    errorBoundaryActionUrl?: string,
    t: TFunction,
    history: Object
};
type State = {
    error: ?Error,
    info: ?Object
};

export type ErrorBoundaryOptionsType = {
    disableErrorResetOnRouteChange?: boolean
};

const errorBoundaryFactory = ({
    Component,
    ErrorScreen,
    defaultProps,
    options
}: {
    Component: React.ComponentType<*>,
    ErrorScreen: React.ComponentType<*>,
    defaultProps: Object,
    options: ?ErrorBoundaryOptionsType
}) => {
    class ErrorBoundary extends React.Component<Props, State> {
        static defaultProps = {
            ...defaultProps
        };
        constructor(props) {
            super(props);
            this.state = {
                error: null,
                info: null
            };
        }

        componentDidMount() {
            const { history } = this.props;

            if (!options?.disableErrorResetOnRouteChange) {
                // reset error on route change
                this.unlisten = history.listen(() => {
                    this.setState(prevState => {
                        if (prevState.error) {
                            return {
                                error: null
                            };
                        }
                    });
                });
            }
        }

        componentDidCatch(error, info) {
            ErrorReporter.notifyErrorBoundaryError(error, info);
            this.setState({ error, info });
        }

        componentWillUnmount() {
            this.unlisten && this.unlisten();
        }

        renderError = () => {
            const {
                errorBoundaryTitleKey,
                errorBoundarySubtitleKey,
                errorBoundaryActionLabel,
                errorBoundaryActionUrl,
                t,
                ...others
            } = this.props;
            const { error } = this.state;
            return (
                <ErrorScreen
                    {...others}
                    t={t}
                    error={error}
                    title={errorBoundaryTitleKey && t(errorBoundaryTitleKey)}
                    subtitle={
                        errorBoundarySubtitleKey && t(errorBoundarySubtitleKey)
                    }
                    actionLabel={errorBoundaryActionLabel}
                    actionUrl={errorBoundaryActionUrl}
                />
            );
        };
        render() {
            const { error } = this.state;
            if (error) {
                return this.renderError();
            }
            const { getBoundaryComponentRef, ...others } = this.props;
            if (Component) {
                return <Component {...others} ref={getBoundaryComponentRef} />;
            }
            return this.props.children;
        }
    }
    return translate()(withRouter(ErrorBoundary));
};

export { errorBoundaryFactory };
export default errorBoundaryFactory;
